import { isSupportedConvertImageFile } from '../convert/convertImageEngine';
import { encodeCanvas, extForBlob } from '../shared/imageOutputFormats';

export { formatFileSize } from '../compress/compressImageEngine';

export const isSupportedCropImageFile = isSupportedConvertImageFile;

export const ASPECT_PRESETS = [
    { id: 'free' },
    { id: '1x1', value: 1 },
    { id: '4x3', value: 4 / 3 },
    { id: '16x9', value: 16 / 9 },
    { id: '3x2', value: 3 / 2 },
];

export const OUTPUT_FORMATS = ['jpg', 'png', 'webp', 'avif'];

async function prepareFile(file) {
    const name = file.name.toLowerCase();
    const isHeic = /\.(heic|heif)$/.test(name) || file.type === 'image/heic' || file.type === 'image/heif';
    if (isHeic) {
        const heic2any = (await import('heic2any')).default;
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        const base = file.name.replace(/\.(heic|heif)$/i, '');
        return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
    }
    return file;
}

const FORMAT_META = {
    jpg: { mime: 'image/jpeg', ext: 'jpg', quality: 0.92, alpha: false },
    png: { mime: 'image/png', ext: 'png', quality: null, alpha: true },
    webp: { mime: 'image/webp', ext: 'webp', quality: 0.92, alpha: true },
    avif: { mime: 'image/avif', ext: 'avif', quality: 0.9, alpha: true },
};

function canvasToBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), mime, quality ?? undefined);
    });
}

/** Quarter turns, normalised — everything else here assumes 0/90/180/270. */
export const normaliseRotation = (rotation) => ((Math.round((rotation || 0) / 90) * 90) % 360 + 360) % 360;

/**
 * A saved crop rectangle turned with its image.
 *
 * The rectangle is stored in percentages of the *current* orientation, so a
 * quarter turn has to move it too — dropping it would throw away the selection
 * every time the picture is straightened.
 *
 * Clockwise, a box at `(x, y, w, h)` lands at `(100 - y - h, x, h, w)`: the old
 * top edge becomes the new right edge.
 */
export function rotateCropPercent(cropPct, turns = 1) {
    if (!cropPct || cropPct.unit !== '%') return cropPct;
    let box = { ...cropPct };
    const steps = ((Math.round(turns) % 4) + 4) % 4;
    for (let i = 0; i < steps; i += 1) {
        box = {
            ...box,
            unit: '%',
            x: Math.max(0, Math.min(100, 100 - (box.y + box.height))),
            y: Math.max(0, Math.min(100, box.x)),
            width: box.height,
            height: box.width,
        };
    }
    return box;
}

/**
 * The image turned upright, as a canvas.
 *
 * The crop rectangle is picked on the *rotated* picture, so rotation has to be
 * baked in before the crop is taken rather than applied to the result.
 * Returns the image itself when there is nothing to turn.
 */
export function rotatedSource(image, rotation) {
    const turn = normaliseRotation(rotation);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!turn) return image;

    const swap = turn % 180 === 90;
    const canvas = document.createElement('canvas');
    canvas.width = swap ? height : width;
    canvas.height = swap ? width : height;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((turn * Math.PI) / 180);
    ctx.drawImage(image, -width / 2, -height / 2);
    return canvas;
}

/**
 * Crop a displayed image element to a pixel crop at natural resolution.
 */
export async function cropImageToBlob(image, pixelCrop, format = 'jpg', rotation = 0) {
    if (!image || !pixelCrop?.width || !pixelCrop?.height) {
        throw new Error('invalid-crop');
    }

    const meta = FORMAT_META[format] || FORMAT_META.jpg;

    const source = rotatedSource(image, rotation);
    const naturalW = source.naturalWidth || source.width || 0;
    const naturalH = source.naturalHeight || source.height || 0;
    if (!naturalW || !naturalH) throw new Error('invalid-image');

    const isNaturalCrop = Boolean(pixelCrop.__natural);

    let sx;
    let sy;
    let sw;
    let sh;
    if (isNaturalCrop) {
        // pixelCrop is already in natural-image pixels.
        sx = pixelCrop.x;
        sy = pixelCrop.y;
        sw = pixelCrop.width;
        sh = pixelCrop.height;
    } else {
        // pixelCrop is in displayed-pixel coordinates; convert to natural pixels.
        const scaleX = naturalW / (image.width || naturalW);
        const scaleY = naturalH / (image.height || naturalH);
        // (A displayed crop is only ever produced upright, so `source` and
        // `image` agree here.)
        sx = pixelCrop.x * scaleX;
        sy = pixelCrop.y * scaleY;
        sw = pixelCrop.width * scaleX;
        sh = pixelCrop.height * scaleY;
    }

    // Clamp crop box into the image bounds.
    sx = Math.max(0, Math.min(sx, naturalW - 1));
    sy = Math.max(0, Math.min(sy, naturalH - 1));
    sw = Math.max(1, Math.min(sw, naturalW - sx));
    sh = Math.max(1, Math.min(sh, naturalH - sy));

    const outW = Math.max(1, Math.round(sw));
    const outH = Math.max(1, Math.round(sh));

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d', { alpha: meta.alpha });

    if (!meta.alpha) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, outW, outH);
    }

    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, outW, outH);
    return canvasToBlob(canvas, meta.mime, meta.quality);
}

/**
 * A small preview of exactly what the crop will produce.
 *
 * The grid used to show the whole picture with a "done" badge, which said
 * nothing about the crop that was actually saved. This renders the chosen
 * region (turned, if the image was rotated) at thumbnail size.
 */
export async function renderCropPreview(file, cropPct, rotation = 0, maxSize = 420) {
    const image = await loadImageElement(file);
    const source = rotatedSource(image, rotation);
    const width = source.naturalWidth || source.width;
    const height = source.naturalHeight || source.height;

    const percent = cropPct?.unit === '%' && cropPct.width && cropPct.height ? cropPct : null;
    const sx = percent ? (percent.x / 100) * width : 0;
    const sy = percent ? (percent.y / 100) * height : 0;
    const sw = percent ? (percent.width / 100) * width : width;
    const sh = percent ? (percent.height / 100) * height : height;

    const scale = Math.min(1, maxSize / Math.max(sw, sh));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sw * scale));
    canvas.height = Math.max(1, Math.round(sh * scale));
    const ctx = canvas.getContext('2d');
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return canvasToBlob(canvas, 'image/webp', 0.86).catch(
        () => canvasToBlob(canvas, 'image/jpeg', 0.86),
    );
}

/** Decode a file into an `<img>` (HEIC converted first). */
export async function loadImageElement(file) {
    const prepared = await prepareFile(file);
    const url = URL.createObjectURL(prepared);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

export async function readImageDimensions(file) {
    const img = await loadImageElement(file);
    return { width: img.naturalWidth, height: img.naturalHeight };
}

export async function cropImageFile(file, imageElement, pixelCrop, format = 'jpg', rotation = 0) {
    const img = imageElement || await loadImageElement(file);
    const meta = FORMAT_META[format] || FORMAT_META.jpg;
    let blob;
    if (format === 'avif') {
        // Chrome's canvas cannot write AVIF — it hands back a PNG without
        // complaint, which used to ship named `.avif`. Crop losslessly first,
        // then let the shared encoder write real AVIF (WebAssembly where needed).
        const png = await cropImageToBlob(img, pixelCrop, 'png', rotation);
        const bitmap = await createImageBitmap(png);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        bitmap.close?.();
        ({ blob } = await encodeCanvas(canvas, 'avif', { quality: meta.quality ?? 0.9 }));
    } else {
        blob = await cropImageToBlob(img, pixelCrop, format, rotation);
    }
    const base = file.name.replace(/\.[^.]+$/, '');

    let outW;
    let outH;
    if (pixelCrop?.__natural) {
        outW = Math.max(1, Math.round(pixelCrop.width));
        outH = Math.max(1, Math.round(pixelCrop.height));
    } else {
        const scaleX = img.naturalWidth / (img.width || img.naturalWidth);
        const scaleY = img.naturalHeight / (img.height || img.naturalHeight);
        outW = Math.max(1, Math.round(pixelCrop.width * scaleX));
        outH = Math.max(1, Math.round(pixelCrop.height * scaleY));
    }

    return {
        name: `${base}-cropped.${extForBlob(blob, meta.ext)}`,
        blob,
        width: outW,
        height: outH,
        originalSize: file.size,
        croppedSize: blob.size,
    };
}

/**
 * An object URL the crop modal can hand to `<img>`.
 *
 * A rotated image is baked into a new blob rather than turned with CSS: the
 * crop rectangle is measured against the element's own box, so a transform
 * would leave the selection and the picture disagreeing.
 */
export async function loadPreparedObjectUrl(file, rotation = 0) {
    const prepared = await prepareFile(file);
    if (!normaliseRotation(rotation)) return URL.createObjectURL(prepared);

    const image = await loadImageElement(prepared);
    const canvas = rotatedSource(image, rotation);
    const blob = await canvasToBlob(canvas, 'image/png', null);
    return URL.createObjectURL(blob);
}

/** HEIC/HEIF → JPEG so `<img>`/canvas can decode it; other formats pass through. */
export const prepareImageFile = prepareFile;
